import { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { hasPlottableCoordinates } from "@/lib/coordinates";
import {
  MAPBOX_TOKEN,
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
} from "@/config/mapbox";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { Card } from "@/components/ui/card";

interface Property {
  id: number;
  title: string;
  city: string;
  price: number;
  image: string;
  lat: number;
  lng: number;
  available: boolean;
  status?: "available" | "booked";
  roommates?: number;
}

interface PropertyMapViewProps {
  properties: Property[];
}

const PropertyMapView = ({ properties }: PropertyMapViewProps) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  );
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(
    null
  );
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(new Set());
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const markerElementsRef = useRef<Map<number, HTMLDivElement>>(new Map());

  // Only plot properties that actually have coordinates. `properties.lat`/`lng`
  // are NOT NULL, so an ungeocoded property arrives here as 0/0 — plotting it
  // drops a pin off West Africa and, worse, drags `fitBounds` down there, so
  // the whole grid zooms out past the Mediterranean to keep it in frame.
  const plottableProperties = useMemo(
    () => properties.filter((p) => hasPlottableCoordinates(p.lat, p.lng)),
    [properties]
  );

  useEffect(() => {
    if (!mapContainer.current || map.current || !MAPBOX_TOKEN) return;

    // Initialize map
    mapboxgl.accessToken = MAPBOX_TOKEN;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center:
        plottableProperties.length > 0
          ? [plottableProperties[0].lng, plottableProperties[0].lat]
          : DEFAULT_MAP_CENTER,
      zoom: plottableProperties.length > 0 ? 11 : DEFAULT_MAP_ZOOM,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

    // Cleanup
    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      map.current?.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    if (!map.current) return;

    // Remove existing markers first, unconditionally — otherwise narrowing a
    // filter down to a set that's entirely ungeocoded (0/0) bails out below
    // before clearing anything, leaving the previous filter's stale pins on
    // the map even though none of those properties are in `properties`
    // anymore.
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    markerElementsRef.current.clear();

    if (plottableProperties.length === 0) return;

    // Add markers for each property
    plottableProperties.forEach((property) => {
      // Create a lightweight root container for Mapbox positioning
      const root = document.createElement("div");
      root.style.cssText = "cursor: pointer;";

      // Create the visual bubble inside the root
      const bubble = document.createElement("div");
      bubble.className = "coliviq-marker-bubble";
      bubble.style.cssText = `
        width: 40px;
        height: 40px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 12px;
        transition: transform 0.15s ease, background-color 0.2s ease, box-shadow 0.15s ease;
        background-color: hsl(var(--primary));
      `;
      bubble.innerHTML = `€${property.price}`;

      // Store bubble element for later styling updates
      markerElementsRef.current.set(property.id, bubble);

      // Apply hover transform only to the bubble
      bubble.addEventListener("mouseenter", () => {
        bubble.style.transform = "scale(1.08)";
      });

      bubble.addEventListener("mouseleave", () => {
        bubble.style.transform =
          selectedPropertyId === property.id ? "scale(1)" : "scale(1)";
      });

      root.appendChild(bubble);

      const marker = new mapboxgl.Marker(root)
        .setLngLat([property.lng, property.lat])
        .addTo(map.current!);

      bubble.addEventListener("click", (e) => {
        e.stopPropagation();
        setSelectedProperty(property);
        setSelectedPropertyId(property.id);

        // Update marker styles
        updateMarkerStyles(property.id);

        // Desktop: Create popup immediately and center it
        if (!isMobile && map.current) {
          // Close existing popup
          if (popupRef.current) {
            popupRef.current.remove();
            popupRef.current = null;
          }

          // Fly to location first
          map.current.flyTo({
            center: [property.lng, property.lat],
            zoom: 14,
            duration: 1000,
          });

          // Build images array (support optional property.images)
          const images: string[] =
            Array.isArray((property as any).images) &&
            (property as any).images.length
              ? (property as any).images
              : [property.image];
          let currentIndex = 0;

          // Create popup content
          const popupContent = document.createElement("div");
          popupContent.className = "coliviq-popup-content";
          popupContent.style.cssText = "max-width: 400px; width: 320px;";
          popupContent.innerHTML = `
            <div class="bg-background rounded-lg shadow-lg overflow-hidden" style="width: 100%; max-width: 100%;">
              <div class="relative group">
                <img src="${images[0]}" alt="${property.title}" class="popup-lead-img w-full h-48 object-cover" />
                
                <!-- Overlay Controls -->
                <button class="popup-close-btn absolute top-3 right-3 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-colors z-10">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
                
                <button class="popup-favorite-btn absolute top-3 left-3 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-colors">
                  <svg class="w-4 h-4" ${favoriteIds.has(property.id) ? 'fill="currentColor" style="color: rgb(239, 68, 68);"' : 'fill="none"'} stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg>
                </button>
                
                <!-- Navigation Arrows -->
                ${
                  images.length > 1
                    ? `
                <button class="popup-prev-btn absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all opacity-0 group-hover:opacity-100">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
                </button>
                <button class="popup-next-btn absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all opacity-0 group-hover:opacity-100">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                </button>`
                    : ""
                }
                
                <!-- Pagination Dots -->
                ${
                  images.length > 1
                    ? `<div class="popup-dots absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  ${images.map((_, i) => `<div data-index="${i}" class="dot w-1.5 h-1.5 rounded-full ${i === 0 ? "bg-white" : "bg-white/50"} shadow-sm"></div>`).join("")}
                </div>`
                    : ""
                }
              </div>
              
              <div class="p-4">
                <h3 class="font-bold text-foreground mb-1">${property.title}</h3>
                <p class="text-sm text-muted-foreground mb-2">${property.city}</p>
                <div class="flex items-center justify-between mb-3">
                  <div>
                    <span class="text-2xl font-bold text-foreground">€${property.price}</span>
                    <span class="text-sm text-muted-foreground">${t("properties.perMonth")}</span>
                  </div>
                  ${property.available ? `<span class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-accent text-accent-foreground">${t("properties.available")}</span>` : ""}
                </div>
                <a href="/property/${property.id}" class="inline-flex items-center justify-center w-full h-9 px-4 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm">
                  ${t("properties.viewDetails")}
                  <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>
                </a>
              </div>
            </div>
          `;

          // Attach handlers
          const imgEl = popupContent.querySelector(
            ".popup-lead-img"
          ) as HTMLImageElement | null;
          const prevBtn = popupContent.querySelector(".popup-prev-btn");
          const nextBtn = popupContent.querySelector(".popup-next-btn");
          const dotsContainer = popupContent.querySelector(".popup-dots");
          const closeBtn = popupContent.querySelector(".popup-close-btn");
          const favoriteBtn = popupContent.querySelector(".popup-favorite-btn");

          const setImage = (index: number) => {
            if (!imgEl) return;
            currentIndex = (index + images.length) % images.length;
            imgEl.src = images[currentIndex];
            // Update dots
            if (dotsContainer) {
              dotsContainer.querySelectorAll(".dot").forEach((dot, i) => {
                (dot as HTMLElement).className =
                  `dot w-1.5 h-1.5 rounded-full ${i === currentIndex ? "bg-white" : "bg-white/50"} shadow-sm`;
              });
            }
          };

          prevBtn?.addEventListener("click", (ev) => {
            ev.stopPropagation();
            setImage(currentIndex - 1);
          });
          nextBtn?.addEventListener("click", (ev) => {
            ev.stopPropagation();
            setImage(currentIndex + 1);
          });
          dotsContainer?.querySelectorAll(".dot").forEach((dot) => {
            dot.addEventListener("click", (ev) => {
              ev.stopPropagation();
              const idx = Number(
                (ev.currentTarget as HTMLElement).dataset.index
              );
              setImage(idx);
            });
          });

          // Close button
          closeBtn?.addEventListener("click", (ev) => {
            ev.stopPropagation();
            if (popupRef.current) {
              popupRef.current.remove();
              popupRef.current = null;
            }
            setSelectedProperty(null);
            setSelectedPropertyId(null);
            markerElementsRef.current.forEach((bubble) => {
              bubble.style.backgroundColor = "hsl(var(--primary))";
              bubble.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
            });
          });

          // Favorite button
          favoriteBtn?.addEventListener("click", (ev) => {
            ev.stopPropagation();
            setFavoriteIds((prev) => {
              const next = new Set(prev);
              if (next.has(property.id)) {
                next.delete(property.id);
              } else {
                next.add(property.id);
              }
              // Update icon UI
              const svg = favoriteBtn.querySelector("svg");
              if (svg) {
                const selected = next.has(property.id);
                svg.setAttribute("fill", selected ? "currentColor" : "none");
                (svg as any).style = selected ? "color: rgb(239, 68, 68);" : "";
              }
              return next;
            });
          });

          // Function to center the popup in the viewport using CSS
          const centerPopup = () => {
            if (!popupRef.current || !map.current) return;

            const popupElement = popupRef.current.getElement();
            if (!popupElement) return;

            const container = map.current.getContainer();
            const containerRect = container.getBoundingClientRect();
            const popupRect = popupElement.getBoundingClientRect();

            // Calculate center of viewport
            const viewportCenterX = containerRect.width / 2;
            const viewportCenterY = containerRect.height / 2;

            // Calculate popup center relative to container
            const popupCenterX =
              popupRect.left - containerRect.left + popupRect.width / 2;
            const popupCenterY =
              popupRect.top - containerRect.top + popupRect.height / 2;

            // Calculate offset needed to center
            const offsetX = viewportCenterX - popupCenterX;
            const offsetY = viewportCenterY - popupCenterY;

            // Apply transform to center the popup (relative to its current position)
            if (Math.abs(offsetX) > 1 || Math.abs(offsetY) > 1) {
              popupElement.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
              popupElement.style.transition = "transform 0.2s ease-out";
            }
          };

          // Get the center of the current viewport
          const center = map.current.getCenter();

          // Create popup at the center coordinates (anchor doesn't matter, we'll center with CSS)
          popupRef.current = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            // @ts-expect-error mapbox-gl has no 'auto' anchor. At runtime it
            // yields an invalid transform, so CSS decides placement; dropping
            // the option would switch to mapbox's computed anchor.
            anchor: "auto",
            offset: [0, 0],
            maxWidth: "none",
            className: "coliviq-map-popup",
          })
            .setLngLat([center.lng, center.lat])
            .setDOMContent(popupContent)
            .addTo(map.current);

          // Center the popup after it's rendered
          requestAnimationFrame(() => {
            setTimeout(() => {
              centerPopup();
            }, 50);
          });

          // Update popup position after flyTo completes and re-center
          map.current.once("moveend", () => {
            if (popupRef.current && map.current) {
              const newCenter = map.current.getCenter();
              popupRef.current.setLngLat([newCenter.lng, newCenter.lat]);

              // Re-center after position update
              requestAnimationFrame(() => {
                setTimeout(() => {
                  centerPopup();
                }, 100);
              });
            }
          });

          // Close when clicking on map outside the popup
          const handleMapClick = (evt: any) => {
            const target = evt?.originalEvent?.target as Node | null;
            if (target && popupContent.contains(target)) return;
            if (popupRef.current) {
              popupRef.current.remove();
              popupRef.current = null;
            }
            setSelectedProperty(null);
            setSelectedPropertyId(null);
            markerElementsRef.current.forEach((bubble) => {
              bubble.style.backgroundColor = "hsl(var(--primary))";
              bubble.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
            });
            map.current?.off("click", handleMapClick as any);
          };
          map.current.on("click", handleMapClick as any);
        }
      });

      markersRef.current.push(marker);
    });

    // Fit bounds to show all properties
    if (plottableProperties.length > 1) {
      const bounds = new mapboxgl.LngLatBounds();
      plottableProperties.forEach((property) => {
        bounds.extend([property.lng, property.lat]);
      });
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 13 });
    }
  }, [plottableProperties, isMobile, t]);

  // Update marker styles based on selection
  const updateMarkerStyles = (selectedId: number) => {
    markerElementsRef.current.forEach((bubble, propertyId) => {
      if (propertyId === selectedId) {
        // Selected state: black background, stronger shadow
        bubble.style.backgroundColor = "#000";
        bubble.style.boxShadow = "0 4px 14px rgba(0,0,0,0.25)";
      } else {
        // Default state: primary color
        bubble.style.backgroundColor = "hsl(var(--primary))";
        bubble.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
      }
    });
  };

  // Clean up popup when selection changes
  useEffect(() => {
    return () => {
      if (popupRef.current) {
        popupRef.current.remove();
      }
    };
  }, [selectedProperty]);

  if (!MAPBOX_TOKEN) {
    return (
      <Card className="p-8 text-center bg-muted/50">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-xl font-bold mb-2">
          {t("common.map.configRequiredTitle", "Map Configuration Required")}
        </h3>
        <p className="text-muted-foreground mb-4">
          {t(
            "common.map.mapboxTokenRequired",
            "The map view requires a Mapbox token to be configured."
          )}
        </p>
        <p className="text-sm text-muted-foreground">
          <strong>{t("common.map.adminLabel", "Admin:")}</strong>{" "}
          {t("common.map.addMapboxTokenIn", "Add your Mapbox token in")}{" "}
          <code className="bg-background px-2 py-1 rounded">
            {t("common.map.mapboxConfigPath", "src/config/mapbox.ts")}
          </code>
        </p>
      </Card>
    );
  }

  return (
    <div className="relative w-full h-[600px] rounded-2xl overflow-hidden">
      <div ref={mapContainer} className="absolute inset-0" />

      {/* Mobile: Bottom Sheet */}
      {isMobile && selectedProperty && (
        <Sheet
          open={!!selectedProperty}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedProperty(null);
              setSelectedPropertyId(null);
              if (popupRef.current) {
                popupRef.current.remove();
              }
              // Reset all marker styles
              markerElementsRef.current.forEach((bubble) => {
                bubble.style.backgroundColor = "hsl(var(--primary))";
                bubble.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
              });
            }
          }}
        >
          <SheetContent side="bottom" className="h-auto max-h-[80vh]">
            <SheetHeader>
              <SheetTitle>{selectedProperty.title}</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <img
                src={selectedProperty.image}
                alt={selectedProperty.title}
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <p className="text-sm text-muted-foreground mb-3">
                {selectedProperty.city}
              </p>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="text-2xl font-bold text-foreground">
                    €{selectedProperty.price}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {" "}
                    {t("properties.perMonth")}
                  </span>
                </div>
                {selectedProperty.available && (
                  <Badge className="bg-accent">
                    {t("properties.available")}
                  </Badge>
                )}
              </div>
              <Link to={`/property/${selectedProperty.id}`}>
                <Button className="w-full">
                  {t("properties.viewDetails")}
                  <ExternalLink className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

export default PropertyMapView;
